import { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";

import { useAuth } from "../../contexts/AuthContext";
import supabase from "../../services/supabase";
import "./Header.css";
import { comprehensiveSearch } from '../../services/mapService';
import { fetchNotifications, subscribeToNotifications, markAllNotificationsRead } from '../../services/notificationService';

export default function Header({ searchQuery, setSearchQuery, onSearch, hideHeader }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut } = useAuth();
  const [profilePicture, setProfilePicture] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const [showNotifications, setShowNotifications] = useState(false);
  const notificationsRef = useRef(null);

  // State for Sign-in/Sign-up modals moved from LandingPage
  const [showSignup, setShowSignup] = useState(false);
  const [showSignin, setShowSignin] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [showSigninEmailForm, setShowSigninEmailForm] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [signinEmail, setSigninEmail] = useState("");
  const [signinPassword, setSigninPassword] = useState("");

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);

  // Autocomplete state
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const suggestionsRef = useRef(null);
  const searchInputRef = useRef(null);

  // Searchbar stuff
  const parseHashtags = (text) => {
    const matches = [...(text || "").matchAll(/#([\w-]+)/g)].map(m => m[1].toLowerCase());
    // Deduplicate
    return Array.from(new Set(matches));
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  useEffect(() => {
    const fetchProfilePicture = async () => {
      if (user) {
        try {
          const { data, error } = await supabase
            .from("user_profiles")
            .select("profilePicture")
            .eq("user_id", user.id)
            .single();

            if (error) {
              console.error("Error fetching profile picture:", error);
              return;
            }

            if (data && data.profilePicture) {
              setProfilePicture(data.profilePicture);
            }
        } catch (error) {
          console.error("Error in fetchProfilePicture:", error);
        }
      }
    };

    fetchProfilePicture();

    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
      if (notificationsRef.current && !notificationsRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [user]);

  // Load notifications and subscribe to realtime
  useEffect(() => {
    let unsubscribe = null;
    (async () => {
      const items = await fetchNotifications(20);
      setNotifications(items);
      unsubscribe = subscribeToNotifications((n) => {
        setNotifications(prev => [n, ...(prev || [])]);
      });
    })();
    return () => { if (typeof unsubscribe === 'function') unsubscribe(); };
  }, [user?.id]);

  const unreadCount = (notifications || []).filter(n => !n.read_at).length;

  // Debounced fetch for location suggestions
  useEffect(() => {
    if (!searchQuery || searchQuery.length < 2 || searchQuery.startsWith("#")) {
      setLocationSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    setSuggestionsLoading(true);
    const controller = new AbortController();
    const timeout = setTimeout(async () => {
      try {
        const suggestions = await comprehensiveSearch(searchQuery, { 
          limit: 8,
          token: process.env.REACT_APP_MAPBOX_TOKEN
        });

        // Add display_name for compatibility with existing code
        const enhancedSuggestions = suggestions.map(suggestion => ({
          ...suggestion,
          display_name: suggestion.place_name
        }));

        setLocationSuggestions(enhancedSuggestions);
        setShowSuggestions(true);
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Error fetching location suggestions:', err);
          setLocationSuggestions([]);
          setShowSuggestions(false);
        }
      } finally {
        setSuggestionsLoading(false);
      }
    }, 300); // debounce
    return () => {
      clearTimeout(timeout);
      controller.abort();
    };
  }, [searchQuery]);

  // Handle suggestion click
  const handleSuggestionClick = (suggestion) => {
    setSearchQuery(suggestion.display_name);
    setShowSuggestions(false);
    searchInputRef.current.blur();
  };

  // Handle keyboard navigation
  const handleSearchInputKeyDown = (e) => {
    if (showSuggestions && locationSuggestions.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedSuggestionIndex((prev) => (prev + 1) % locationSuggestions.length);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedSuggestionIndex((prev) => (prev - 1 + locationSuggestions.length) % locationSuggestions.length);
      } else if (e.key === "Enter") {
        if (selectedSuggestionIndex >= 0 && selectedSuggestionIndex < locationSuggestions.length) {
          handleSuggestionClick(locationSuggestions[selectedSuggestionIndex]);
          return;
        }
        handleSearchSubmit(e);
      } else if (e.key === "Escape") {
        setShowSuggestions(false);
      }
    } else if (e.key === "Enter") {
      handleSearchSubmit(e);
    }
  };

  // Handle search submit
  const handleSearchSubmit = (e) => {
    if (e) e.preventDefault();
    setShowSuggestions(false);

    const hashtags = parseHashtags(searchQuery);

    if (hashtags.length > 0) {
      // Send hashtags to LocalMap; keep original input for convenience if you like
      const qs = new URLSearchParams();
      qs.set("hashtags", hashtags.join(","));
      navigate(`/localmap?${qs.toString()}`);
      return;
    }

    // Existing behavior for geocode search
    if (location.pathname !== "/localmap") {
      navigate(`/localmap?query=${encodeURIComponent(searchQuery)}`);
    } else {
      if (onSearch) onSearch(searchQuery);
    }
  };

  // Hide suggestions on blur
  const handleInputBlur = (e) => {
    setTimeout(() => setShowSuggestions(false), 150); // Delay to allow click
  };

  // --- Auth Handlers Moved from LandingPage ---

  const handleEmailSignup = async () => {
    try {
      const { error: signupError } = await supabase.auth.signUp({
        email,
        password,
      });

      if (signupError) throw signupError;

      // Attempt to fetch user immediately, might depend on email confirmation
      const {
        data: { user: newUser }, // Renamed to avoid conflict with context user
        error: userFetchError,
      } = await supabase.auth.getUser();
      
      if (userFetchError || !newUser) {
        console.warn("User not immediately available. Check email confirmation settings. Redirecting to profile setup anyway.");
        // Proceed to profile setup even if getUser fails immediately after signup,
        // as the user might exist but requires email confirmation.
        // The profile setup page should handle cases where the user isn't fully authenticated yet.
      } else {
        // Insert placeholder user profile data only if user fetch is successful
        await supabase.from("user_profiles").insert({
          user_id: newUser.id,
          email: newUser.email,
          username: newUser.email.split("@")[0],
          name: "",
          profilePicture: "",
          bio: "",
          homeLocation: "",
        });
      }

      setShowSignup(false); // Close modal
      navigate("/profile-setup");
    } catch (error) {
      console.error("Signup error:", error.message);
      alert(`Signup failed: ${error.message}`);
    }
  };

  const handleEmailSignin = async () => {
    try {
      console.log("Signing in with email:", signinEmail);
      const { data, error } = await supabase.auth.signInWithPassword({
        email: signinEmail,
        password: signinPassword,
      });

      if (error) throw error;
      
      console.log("Sign in successful:", !!data.user);
      
      if (!data || !data.user) {
        console.error("Login successful but user data is missing");
        alert("Login succeeded but user profile couldn't be loaded. Please try again.");
        return;
      }
      
      // Close modal and navigate to home or feed after successful sign-in
      setShowSignin(false);
      navigate("/"); // Or navigate('/feed') or wherever appropriate

    } catch (error) {
      console.error("Signin error:", error.message);
      alert("Login failed: " + error.message);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          // Redirect to profile setup after Google Auth
          // Supabase handles both sign-up and sign-in cases here
          redirectTo: window.location.origin, 
        },
      });

      if (error) throw error;
      // No need to close modal manually, redirect happens
    } catch (error) {
      console.error("Google login failed:", error.message);
      alert(`Google login failed: ${error.message}`);
    }
  };

  // --- End Auth Handlers ---

  const handleLogout = async () => {
    try {
      setShowDropdown(false);
      setIsMobileMenuOpen(false); // Close mobile menu on logout
      await signOut();
      navigate("/");
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const handleProfileClick = () => {
    setShowDropdown(!showDropdown);
  };

  const handleFeedClick = () => {
    navigate("/feed");
  };

  const handleProfileNav = async () => {
    if (!user) return;
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('username')
        .eq('user_id', user.id)
        .single();
      if (error || !data || !data.username) {
        navigate('/profile'); // fallback
      } else {
        navigate(`/profile/${data.username}`);
      }
      setShowDropdown(false);
      setIsMobileMenuOpen(false);
    } catch (err) {
      navigate('/profile');
      setShowDropdown(false);
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <> {/* Use Fragment to wrap Header and Modals */}
      <header className={`header${hideHeader ? ' header-retract' : ''}`}>
        <div className="logo-title" onClick={() => navigate("/")}>

          <img src="/mascot-nobg.png" alt="Logo" className="logo" />
          {/* <img src="/pridemonth_penguin.png" alt="Logo" className="logo" /> */}
          <h1 className="site-title">Pioneer</h1>
        </div>
        {location.pathname !== '/' && (
        <form className="search-container" onSubmit={handleSearchSubmit} autoComplete="off">
          <div className="search-dropdown-wrapper">
            <input
              type="search"
              className="search-input"
              placeholder="Search for spots..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setSelectedSuggestionIndex(-1);
              }}
              onKeyDown={handleSearchInputKeyDown}
              onBlur={handleInputBlur}
              ref={searchInputRef}
              autoComplete="off"
            />
            {showSuggestions && locationSuggestions.length > 0 && (
              <ul className="suggestions-dropdown" ref={suggestionsRef}>
                {locationSuggestions.map((suggestion, idx) => (
                  <li
                    key={suggestion.place_id}
                    className={
                      "suggestion-item" +
                      (idx === selectedSuggestionIndex ? " suggestion-item-selected" : "")
                    }
                    onMouseDown={() => handleSuggestionClick(suggestion)}
                  >
                    {suggestion.display_name}
                  </li>
                ))}
              </ul>
            )}
          </div>
          <button type="submit" className="search-button">
            Search
          </button>
        </form>
        )}

        {user && (
          <button className="hamburger-icon" onClick={toggleMobileMenu}>
            <div className="hamburger-line"></div>
            <div className="hamburger-line"></div>
            <div className="hamburger-line"></div>
          </button>
        )}

        <div className="auth-buttons">
          {user ? (
            <>
              <button className="post-button desktop-only" onClick={() => navigate("/new-post")}>Post</button>
              <button className="feed-button desktop-only" onClick={handleFeedClick}>Feed</button>
              <button className="find-people-button desktop-only" onClick={() => navigate("/find-people")}>Find People</button>
              <button className="map-button desktop-only" onClick={() => navigate("/localmap")}>Map</button>
              <button className="leaderboards-button desktop-only" onClick={() => navigate("/leaderboards")}>Leaderboards</button>
              <div className="notifications-container desktop-only" ref={notificationsRef}>
                <button
                  className={`notifications-btn${showNotifications ? ' active' : ''}`}
                  aria-label="Notifications"
                  onClick={() => setShowNotifications(v => !v)}
                  type="button"
                >
                  <span className="bell-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" role="img" focusable="false">
                      <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                      <path d="M18 8a6 6 0 0 0-12 0v3a2 2 0 0 1-.85 1.64L3 14h18l-2.15-1.36A2 2 0 0 1 18 11V8"/>
                    </svg>
                  </span>
                  {unreadCount > 0 && (
                    <span className="notifications-badge" aria-label={`${unreadCount} unread notifications`}>{unreadCount > 9 ? '9+' : unreadCount}</span>
                  )}
                </button>
                {showNotifications && (
                  ReactDOM.createPortal(
                    <div className="notifications-dropdown" style={{ position: 'absolute', top: 'calc(85px + 10px)', right: 'calc(2.2rem + 56px)' }}>
                      <div className="notifications-header">
                        <span>Notifications</span>
                        <button className="notifications-mark-all" onClick={async () => { await markAllNotificationsRead(); setNotifications(prev => (prev || []).map(n => ({ ...n, read_at: n.read_at || new Date().toISOString() }))); }}>Mark all as read</button>
                      </div>
                      <div className="notifications-list">
                        {(notifications && notifications.length > 0) ? notifications.map(n => (
                          <div key={n.id} className={`notification-item${n.read_at ? '' : ' unread'}`}>
                            <div className="notification-text">
                              {n.type === 'post_like' && 'Someone liked your post'}
                              {n.type === 'post_comment' && 'Someone commented on your post'}
                              {n.type === 'follow' && 'You have a new follower'}
                              {n.type === 'route_invite' && 'You were invited to a route'}
                            </div>
                            <div className="notification-meta">{new Date(n.created_at).toLocaleString()}</div>
                          </div>
                        )) : (
                          <div className="notifications-empty">No notifications</div>
                        )}
                      </div>
                    </div>,
                    document.body
                  )
                )}
              </div>
              <div className="profile-pic-container desktop-only" ref={dropdownRef}>
                <div onClick={handleProfileClick}>
                  {profilePicture ? (
                    <img 
                      src={profilePicture} 
                      alt="Profile" 
                      className="header-profile-pic" 
                    />
                  ) : (
                    <div className="header-profile-pic-placeholder">
                      {user.email ? user.email.charAt(0).toUpperCase() : "?"}
                    </div>
                  )}
                </div>
                
                {showDropdown && (
                  ReactDOM.createPortal(
                    <div className="profile-dropdown" ref={dropdownRef} style={{ position: 'absolute', top: 'calc(85px + 10px)', right: '2.2rem' }}>
                      <div className="dropdown-item profile" onClick={handleProfileNav}>
                        Profile
                      </div>
                      <div className="dropdown-item settings" onClick={() => { navigate('/profile', { state: { initialTab: 'settings' } }); setShowDropdown(false); }}>
                        Settings
                      </div>
                      <div className="dropdown-item logout" onClick={handleLogout}>
                        Logout
                      </div>
                    </div>,
                    document.body
                  )
                )}
              </div>
            </>
          ) : (
            <>
              <button className="login-button" onClick={() => setShowSignin(true)}>Sign In</button>
              <button className="login-button" onClick={() => setShowSignup(true)}>Sign Up</button>
            </>
          )}
        </div>
      </header>

      {isMobileMenuOpen && user && (
        <div className="mobile-nav-overlay" onClick={toggleMobileMenu}>
          <div className="mobile-nav-content" onClick={(e) => e.stopPropagation()}>
            <button className="mobile-close-btn" onClick={toggleMobileMenu}>&times;</button>
            <div className="mobile-user-info">
              {profilePicture ? (
                <img src={profilePicture} alt="Profile" className="mobile-profile-pic" />
              ) : (
                <div className="mobile-profile-pic-placeholder">
                  {user.email ? user.email.charAt(0).toUpperCase() : "?"}
                </div>
              )}
              <span className="mobile-user-email">{user.email}</span>
            </div>
            <div className="mobile-menu">
              <button className="mobile-menu-item" onClick={() => { navigate("/feed"); setIsMobileMenuOpen(false); }}>Feed</button>
              <button className="mobile-menu-item" onClick={() => { navigate("/new-post"); setIsMobileMenuOpen(false); }}>Post</button>
              <button className="mobile-menu-item" onClick={() => { navigate("/find-people"); setIsMobileMenuOpen(false); }}>Find People</button>
              <button className="mobile-menu-item" onClick={() => { navigate("/localmap"); setIsMobileMenuOpen(false); }}>Map</button>
              <button className="mobile-menu-item" onClick={() => { navigate("/leaderboards"); setIsMobileMenuOpen(false); }}>Leaderboards</button>
              <button className="mobile-menu-item" onClick={handleProfileNav}>Profile</button>
              <button className="mobile-menu-item" onClick={handleLogout}>Logout</button>
            </div>
          </div>
        </div>
      )}

      {/* Sign Up Overlay - Moved from LandingPage */}
      {showSignup && (
        <div className="signup-overlay" onClick={() => setShowSignup(false)}>
          <div
            className="signup-card"
            style={{ width: 450, height: 678 }}
            onClick={(e) => e.stopPropagation()}
          >
            <button className="close-signup" onClick={() => setShowSignup(false)}>×</button>


            <img src="/mascot-nobg.png" alt="Mascot" className="signup-mascot" />
            {/* <img src="/pridemonth_penguin.png" alt="Mascot" className="signup-mascot" /> */}
            {showEmailForm ? (
              <div className="email-form">
                <div className="input-container">
                  <input
                    type="email"
                    placeholder="Email"
                    className="signup-input"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                  />
                </div>
                <div className="input-container">
                  <input
                    type="password"
                    placeholder="Password"
                    className="signup-input"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="new-password"
                  />
                </div>
                <button className="signup-btn email" onClick={handleEmailSignup}>Sign up</button>
              </div>
            ) : (
              <>
                <h2 className="signup-title">
                  Welcome On<br />Board Pioneers!
                </h2>
                <button className="signup-btn google" onClick={handleGoogleLogin}>
                  <img src={`${process.env.PUBLIC_URL}/google.png`} alt="Google logo" />
                  Continue with Google
                </button>
                <button className="signup-btn email" onClick={() => setShowEmailForm(true)}>
                  ✉️ Continue with Email
                </button>
              </>
            )}

            <div className="signup-legal-text">
              <p className="signup-legal">
                by proceeding, you agree to our <a href="/terms">Terms of Use</a> and confirm you have read our
                <a href="/privacy"> Privacy and Cookie Statement</a>
              </p>
              <p className="signup-legal small">
                This site is protected by reCAPTCHA and the Google
                <a href="https://policies.google.com/privacy"> Privacy Policy</a> and <a href="https://policies.google.com/terms">Terms of Service</a> apply.
              </p>
            </div>

            <img
              src={`${process.env.PUBLIC_URL}/wave.png`}
              alt="Wave background"
              className="signup-wave-img"
            />
          </div>
        </div>
      )}

      {/* Sign In Overlay - Moved from LandingPage */}
      {showSignin && (
        <div className="signup-overlay" onClick={() => setShowSignin(false)}>
          <div
            className="signup-card"
            style={{ width: 450, height: 678 }}
            onClick={(e) => e.stopPropagation()}
          >
            <button className="close-signup" onClick={() => setShowSignin(false)}>×</button>


            <img src="/mascot-nobg.png" alt="Mascot" className="signup-mascot" />
            {/* <img src="/pridemonth_penguin.png" alt="Mascot" className="signup-mascot" /> */}
            {showSigninEmailForm ? (
              <div className="email-form">
                <div className="input-container">
                  <input
                    type="email"
                    placeholder="Email"
                    className="signup-input"
                    value={signinEmail}
                    onChange={(e) => setSigninEmail(e.target.value)}
                    autoComplete="email"
                  />
                </div>
                <div className="input-container">
                  <input
                    type="password"
                    placeholder="Password"
                    className="signup-input"
                    value={signinPassword}
                    onChange={(e) => setSigninPassword(e.target.value)}
                     autoComplete="current-password"
                  />
                </div>
                <button className="signup-btn email" onClick={handleEmailSignin}>Sign in</button>
              </div>
            ) : (
              <>
                <h2 className="signup-title">
                  Welcome Back<br />Pioneer!
                </h2>
                <button className="signup-btn google" onClick={handleGoogleLogin}>
                  <img src={`${process.env.PUBLIC_URL}/google.png`} alt="Google logo" />
                  Continue with Google
                </button>
                <button className="signup-btn email" onClick={() => setShowSigninEmailForm(true)}>
                  ✉️ Continue with Email
                </button>
              </>
            )}

            <div className="signup-legal-text">
              <p className="signup-legal">
                by proceeding, you agree to our <a href="/terms">Terms of Use</a> and confirm you have read our
                <a href="/privacy"> Privacy and Cookie Statement</a>
              </p>
              <p className="signup-legal small">
                This site is protected by reCAPTCHA and the Google
                <a href="https://policies.google.com/privacy"> Privacy Policy</a> and <a href="https://policies.google.com/terms">Terms of Service</a> apply.
              </p>
            </div>

            <img
              src={`${process.env.PUBLIC_URL}/wave.png`}
              alt="Wave background"
              className="signup-wave-img"
            />
          </div>
        </div>
      )}
    </> // Close Fragment
  );
}
